import EmblaCarousel from "embla-carousel";

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.variantPickers = this.querySelectorAll(
      ".color-variant-picker button",
    );
    this.productImage = this.querySelector(".product-img img");
    this.productImagePictureSources = this.querySelectorAll(
      ".product-img picture source",
    );
    this.productUrls = this.querySelectorAll('a[href*="/products/"]');
    this.wishlistBtn = this.querySelector("wishlist-button");

    // Setup listeners
    this.variantPickers.forEach((button) => {
      button.addEventListener("click", this.onVariantChange.bind(this));
    });
  }

  connectedCallback() {
    this.initCarousel();
  }
  disconnectedCallback() {
    this.emblaApi.destroy();
  }
  onVariantChange(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const {
      variantId,
      productHandle,
      image,
      price,
      formattedPrice,
      formattedCompareAtPrice,
      discountPercentage,
      productUrl,
    } = button.dataset;

    // Visual feedback on active button (optional but good UI)
    this.variantPickers.forEach((btn) => btn.classList.remove("active"));
    button.classList.add("active");

    // Update image
    if (image) {
      if (this.productImage) {
        this.productImage.src = image;
        this.productImage.srcset = image;
      }
      this.productImagePictureSources.forEach((source) => {
        source.srcset = image;
      });
    }

    // Update URLs
    if (productUrl) {
      this.productUrls.forEach((a) => {
        // Avoid changing the buttons in the variant picker if they are wrapped in an anchor
        if (!a.closest(".color-variant-picker")) {
          a.href = productUrl;
        }
      });
    }


    // Update wishlist button component
    if (this.wishlistBtn) {
      if (variantId)
        this.wishlistBtn.setAttribute("data-variant-id", variantId);
      if (productHandle)
        this.wishlistBtn.setAttribute("data-product-handle", productHandle);
      if (image) this.wishlistBtn.setAttribute("data-image", image);
      if (price) this.wishlistBtn.setAttribute("data-price", price);
      if (productUrl)
        this.wishlistBtn.setAttribute("data-product-url", productUrl);
    }

    // Update direct Add to Cart links
    const addToCartLinks = this.querySelectorAll(
      'a[href^="/cart/add?id="]',
    );
    addToCartLinks.forEach((link) => {
      if (variantId) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set("id", variantId);
        link.href = url.pathname + url.search;
      }

      if (button.dataset.variantAvailable === "true") {
        link.removeAttribute("hidden");
      } else {
        link.setAttribute("hidden", "");
      }
    });

    // Update price display
    const priceContainer = this.querySelector(".product-price");
    const priceBox = this.querySelector(".product-price .price-box");

    if (priceContainer && priceBox && formattedPrice) {
      if (formattedCompareAtPrice && discountPercentage) {
        priceContainer.classList.add("has-discount");
        priceBox.innerHTML = `
              <span class="price">${formattedPrice}</span>
              <span class="original-price"><s>${formattedCompareAtPrice}</s></span>
              <span class="discount-percentage">(${discountPercentage})</span>
            `;
      } else {
        priceContainer.classList.remove("has-discount");
        priceBox.innerHTML = `
              <span class="price">${formattedPrice}</span>
            `;
      }
    }

    // Remove focus from the card controls after a color change
    // so focus-based color pickers can close.
    this.releaseFocus();
  }

  releaseFocus() {
    requestAnimationFrame(() => {
      if (document.activeElement && this.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    });
  }

  initCarousel() {
    const carousels = this.querySelectorAll(".product-card-main");

    if (!carousels.length) return;

    const viewportNode = this.querySelector(".embla__viewport");
    const dotsNode = this.querySelector(".embla__dots");
    const totalSlide = this.querySelectorAll(".embla__slide").length;
    this.prevButtonNode = this.querySelector('.embla__prev');
    this.nextButtonNode = this.querySelector('.embla__next');

    if (!this.prevButtonNode || !this.nextButtonNode) return;

    if (!viewportNode || !dotsNode || totalSlide <= 1) return;

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    const emblaApi = EmblaCarousel(viewportNode, {
      loop: isMobile,
      align: "start",
    });

    this.emblaApi = emblaApi;

    this.resizeHandler = () => {
      const nowMobile = window.matchMedia("(max-width: 1024px)").matches;
      emblaApi.reInit({ loop: nowMobile });
    };
    window.addEventListener("resize", this.resizeHandler);

    this.addDotButtonAndClickHandlers(emblaApi, dotsNode);
    this.addPrevNextButtons(emblaApi, this.prevButtonNode, this.nextButtonNode);
  }

  toggleButtonsDisabled(prevBtn, nextBtn, emblaApi) {
    prevBtn.toggleAttribute('disabled', !emblaApi.canScrollPrev());
    nextBtn.toggleAttribute('disabled', !emblaApi.canScrollNext());
  }

  addPrevNextButtons(emblaApi, prevButtonNode, nextButtonNode) {
    const scrollPrev = (event) => {
      event.preventDefault();
      emblaApi.scrollPrev();
    };
    const scrollNext = (event) => {
      event.preventDefault();
      emblaApi.scrollNext();
    };

    prevButtonNode.addEventListener('click', scrollPrev, false);
    nextButtonNode.addEventListener('click', scrollNext, false);

    const updateButtons = (emblaApi) => {
      this.toggleButtonsDisabled(prevButtonNode, nextButtonNode, emblaApi);
    };

    updateButtons(emblaApi);

    emblaApi.on('select', updateButtons);
    emblaApi.on('reInit', updateButtons);
  }

  addDotButtonAndClickHandlers(emblaApi, dotsNode) {
    let dotNodes = [];
    let index = 0;

    const addDotBtnsWithClickHandlers = () => {
      dotsNode.innerHTML = emblaApi
        .scrollSnapList()
        .map(() => `<button class="embla__dot" type="button" aria-label="Show Image ${index++}"></button>`)
        .join("");

      const scrollTo = (index) => {
        emblaApi.scrollTo(index);
      };

      dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
      dotNodes.forEach((dotNode, index) => {
        dotNode.addEventListener("click", () => scrollTo(index), false);
      });
    };

    const toggleDotButtonsActive = () => {
      const previous = emblaApi.previousScrollSnap();
      const selected = emblaApi.selectedScrollSnap();

      if (dotNodes[previous]) {
        dotNodes[previous].classList.remove("embla__dot--selected");
      }

      if (dotNodes[selected]) {
        dotNodes[selected].classList.add("embla__dot--selected");
      }
    };

    addDotBtnsWithClickHandlers();
    toggleDotButtonsActive();

    emblaApi
      .on("reInit", addDotBtnsWithClickHandlers)
      .on("reInit", toggleDotButtonsActive)
      .on("select", toggleDotButtonsActive);
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}